import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { useDispatch } from 'react-redux';

import TextArea from 'components/TextArea';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import Input from 'components/Input';
import Radio from 'components/Radio';
import Card from 'components/Card';

import { SugestaoPauta } from 'pages/@types';
import api from 'services/api';
import AppError from 'utils/AppError';
import getValidationErrors from 'services/getValidationErros';
import { show } from 'store/modules/dialog/actions';

import { Container, Form, FormGroups } from './styles';
// configuração do validator
const schema = Yup.object().shape({
  titulo: Yup.string()
    .required('Campo Obrigatório')
    .max(30, 'Titulo excedeu o limite de 26 caracteres'),
  pautainfo: Yup.string().required('Campo Obrigatório'),
  programa: Yup.string()
    .required('Campo Obrigatório')
    .nullable(),
});

export default function SugestaoPautaStore() {
  // react-router-dom
  const navigate = useNavigate();
  /**
   * USE STATE
   */
  const [initialData, setInitialData] = useState({} as SugestaoPauta);
  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [outroPrograma, setOutroPrograma] = useState(false);
  const { sugestaoPautaId } = useParams<{ sugestaoPautaId: string }>();

  /**
   * REDUX
   */
  const dispatch = useDispatch();
  /**
   * USE REF
   */
  const formRef = useRef<FormHandles>(null);

  /**
   * FUNÇÕES
   */
  const handleSuccessMessage = useCallback(
    ({ contentHTML, redirectTo }) => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async sugestao => {
      try {
        if (formRef.current) {
          setLoadingSubmit(true);
          formRef.current.setErrors({});
          await schema.validate(sugestao, { abortEarly: false });
          const programa =
            sugestao.programa === 'OUTRO'
              ? sugestao.outroprograma
              : sugestao.programa;
          delete sugestao.outroprograma;
          // Envia requisição de criação ou edição para o backend
          const response = await api.post('/sugestaopauta', {
            ...sugestao,
            programa,
          });

          // Redireciona usuário em caso de sucesso
          if (response && response.data)
            handleSuccessMessage({
              contentHTML: `<h3>Sugestão de Pauta salva!</h3> <p>Você será redirecionado para a lista de sugestões em 3 segundos</p>`,
              redirectTo: `/sugestaopauta`,
            });
          else toast.error('Falha ao salvar a sugestão de pauta');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingSubmit(false);
    },
    [handleSuccessMessage],
  );

  useEffect(() => {
    async function loadSugestao() {
      setLoading(true);
      try {
        const response = await api.get(`/sugestaopauta/${sugestaoPautaId}`);
        if (response && response.data) {
          setInitialData({
            ...response.data,
            titulo: response.data.titulo,
            pautainfo: response.data.pautainfo,
          });
        } else toast.error('Falha de comunicação com o servidor');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    // Verifica se é edição
    if (sugestaoPautaId) {
      loadSugestao();
    }
  }, [sugestaoPautaId]);

  return (
    <Container>
      <Card>
        <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
          <div>
            <header>
              <h4>CRIAR NOVA SUGESTÃO DE PAUTA</h4>
            </header>
            <FormGroups $columns="2fr">
              {!loading && (
                <div>
                  <section>
                    <h5>Título: </h5>
                  </section>
                  <Input name="titulo" placeholder="Título *" />
                  <section>
                    <h5>Qual programa tem a cara dessa história? *</h5>
                  </section>
                  <Radio
                    columns="1fr 1fr"
                    name="programa"
                    options={[
                      { id: 'MAIS VOCÊ', label: 'MAIS VOCÊ' },
                      { id: 'ENCONTRO', label: 'ENCONTRO' },
                      { id: 'É DE CASA', label: 'É DE CASA' },
                      {
                        id: 'CALDEIRÃO',
                        label: 'CALDEIRÃO (sugestão para quadros)',
                      },
                      {
                        id: 'DOMINGÃO',
                        label: 'DOMINGÃO (sugestão para quadros)',
                      },
                      {
                        id: 'PEQUENAS EMPRESAS & GRANDES NEGÓCIOS',
                        label: 'PEQUENAS EMPRESAS & GRANDES NEGÓCIOS',
                      },
                      { id: 'OUTRO', label: 'OUTRO' },
                    ]}
                    handleSelected={value =>
                      setOutroPrograma(value.label === 'OUTRO')
                    }
                  />
                  {outroPrograma && (
                    <div>
                      <section>
                        <h5>Qual outro programa?</h5>
                      </section>
                      <Input name="outroprograma" placeholder="Digite aqui!" />
                    </div>
                  )}
                  <section>
                    <h5>Conta mais sobre a história! *</h5>
                  </section>
                  <TextArea
                    name="pautainfo"
                    height="calc(100vh - 500px)"
                    icon=""
                    errorField=""
                    placeholder="Lembre-se de incluir nomes, o que a pessoa/empresa faz de legal, por que isso é interessante para o nosso estado (e quais motivos para colocar essa história no âmbito nacional!) *"
                  />
                </div>
              )}
            </FormGroups>
          </div>
          <div className="ButtonGroup">
            <Button
              color="primary"
              onClick={e => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              <FiArrowLeft />
              VOLTAR
            </Button>
            <Button className="submitButton" type="submit" color="primary">
              <FiSave />
              {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
